import type { TtqPipelineStatus, TtqSession, TtqSessionParticleEntry } from '../../services/ttqService';
import type { TtqParticleNumberBook } from './ttqibParticleNumbers';

export type TtqibViewport = {
  x: number;
  y: number;
  zoom: number;
};

export type TtqibNodePosition = {
  x: number;
  y: number;
};

export type TtqibNodeKind =
  | 'anchor'
  | 'path_run'
  | 'derived'
  | 'interleaved'
  | 'pipeline_step'
  | 'aggregate_a'
  | 'aggregate_b'
  | 'cttq'
  | 'pap';

export type TtqibCanvasObjectType =
  | 'particle-whole'
  | 'text-segment'
  | 'embedded-image';

export type TtqibCanvasObject = {
  id: string;
  type: TtqibCanvasObjectType;
  layerId: string;
  particleId: string;
  segmentId?: string;
  segmentIndex?: number;
  x: number;
  y: number;
  width?: number;
  height?: number;
  hidden?: boolean;
};

export type TtqibLayer = {
  id: string;
  label: string;
  parentLayerId?: string;
  sourceParticleIds: string[];
  z: number;
  archived?: boolean;
};

export type TtqibEdgeEndpoint = {
  objectId: string;
  layerId: string;
  z: number;
};

export type TtqibEdge = {
  id: string;
  layerId: string;
  source: TtqibEdgeEndpoint;
  target: TtqibEdgeEndpoint;
  pathRunId?: string;
};

/** @deprecated 旧结构视图节点 — 迁移用 */
export type TtqibNodeLayout = {
  nodeId: string;
  kind: TtqibNodeKind;
  position: TtqibNodePosition;
  collapsed?: boolean;
  refId?: string;
};

export type TtqibState = {
  viewport: TtqibViewport;
  viewportInitialized?: boolean;
  activeLayerId?: string;
  layers: TtqibLayer[];
  objects: TtqibCanvasObject[];
  edges: TtqibEdge[];
  layoutRevision?: number;
  updatedAt?: string;
  /** 一解多题搜索关联边（本地持久化） */
  associations?: Array<{ sourceParticleId: string; linkedParticleId: string }>;
  /** 颗粒显示编号（本地持久：删除不回收） */
  particleNumbers?: TtqParticleNumberBook;
  /** 鸟瞰已钉住浮窗 */
  birdsEyePinnedFloats?: Array<{ particleId: string; left: number; top: number }>;
  /** 会话合并后服务端强制重布局时间戳；本地更旧则丢弃本地布局 */
  postMergeRelayoutAt?: string | null;
  /** @deprecated 迁移后忽略 */
  nodes?: TtqibNodeLayout[];
};

export type TtqibCanvasObjectView = TtqibCanvasObject & {
  text: string;
  particleEntry?: TtqSessionParticleEntry;
  particleTitle?: string;
};

export type TtqibNativeCanvasModel = {
  state: TtqibState;
  layers: TtqibLayer[];
  objects: TtqibCanvasObjectView[];
  edges: TtqibEdge[];
};

/** @deprecated 结构视图遗留 */
export type TtqibCanvasNode = {
  layout: TtqibNodeLayout;
  title: string;
  subtitle?: string;
  pipelineStatus?: TtqPipelineStatus;
  particleEntryId?: string;
  particleId?: string;
  particleQuestions?: string | null;
  particleMetadata?: Record<string, unknown> | null;
  pipelineEntry?: TtqSessionParticleEntry | null;
  imageOpen?: boolean;
  selectedForBehavior?: boolean;
  onOpenParticleDetail?: (nodeId: string) => void;
  onInspectTaste?: (nodeId: string) => void;
  onToggleImageOpen?: (particleId: string) => void;
  onToggleBehaviorParticle?: (particleId: string) => void;
  onToggleAdvancedMode?: (nodeId: string) => void;
  onInspectPipelineStep?: (nodeId: string, stepOrder: number) => void;
};

/** @deprecated 结构视图遗留 */
export type TtqibCanvasEdge = {
  id: string;
  fromNodeId: string;
  toNodeId: string;
  type?: 'default' | 'cttq-convergence';
};

/** @deprecated 结构视图遗留 */
export type TtqibGraphModel = {
  state: TtqibState;
  nodes: TtqibCanvasNode[];
  edges: TtqibCanvasEdge[];
};

export type TtqibBuildContext = {
  session: TtqSession;
  savedState?: Partial<TtqibState> | null;
};

export const TTQIB_LAYER_SOURCE_PREFIX = 'layer:source:';
export const TTQIB_LAYER_STANDALONE_PREFIX = 'layer:standalone:';
export const TTQIB_DERIVED_LAYER_PREFIX = 'derived-layer-';

export function derivedLayerId(pathRunId: string): string {
  return `${TTQIB_DERIVED_LAYER_PREFIX}${pathRunId}`;
}

export function wholeObjectId(particleId: string): string {
  return `obj:whole:${particleId}`;
}

export function segmentObjectId(particleId: string, segmentIndex: number): string {
  return `obj:seg:${particleId}:${segmentIndex}`;
}

/** 答案爆炸段：与题目段分轨，避免 segmentIndex 冲突 */
export function answerSegmentObjectId(particleId: string, segmentIndex: number): string {
  return `obj:aseg:${particleId}:${segmentIndex}`;
}

export function imageObjectId(particleId: string): string {
  return `obj:image:${particleId}`;
}

export function mintSegmentId(particleId: string, segmentIndex: number): string {
  return `seg:${particleId}:${segmentIndex}`;
}

export function mintAnswerSegmentId(particleId: string, segmentIndex: number): string {
  return `aseg:${particleId}:${segmentIndex}`;
}
